import React from 'react';

import { Money, convertMoneyToFloat, parseMoney } from '@betterment/core';
import { Box, Button, FormikField, H1, Input } from '@betterment/samba-react';
import { WorkflowLayout } from '@betterment/shared-components';
import { Form, Formik, useFormikContext } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import CoverageCopy from '../../../../Shared/CoverageCopy';
import { useEmergencyFundStep } from '../FlowConfig';

const schema = Yup.object({
  targetAmount: Yup.string()
    .required()
    .test('number over 1', 'Amount must be at least $1', ta => parseFloat(ta) > 1),
});

type FormValues = Yup.InferType<typeof schema>;

interface CustomCoverageInputProps {
  monthlyExpensesAmount: number;
}

const CustomCoverageInput: React.FC<CustomCoverageInputProps> = ({ monthlyExpensesAmount }) => {
  const { values, touched } = useFormikContext<FormValues>();

  const coverage = convertMoneyToFloat(parseMoney(values.targetAmount) || Money(0)) / monthlyExpensesAmount;
  const coverageCopy = touched.targetAmount && <CoverageCopy coverage={coverage} coverageMonthsCap={12} />;

  return (
    <FormikField name="targetAmount">
      <FormikField.Label>Custom savings target</FormikField.Label>
      <FormikField.Control>
        <Input appearance="default" spacing="default" mask="money" />
      </FormikField.Control>
      <FormikField.Description>{coverageCopy}</FormikField.Description>
      <FormikField.ErrorMessage />
    </FormikField>
  );
};

export const CustomCoverageStep: React.FC = () => {
  const navigate = useNavigate();
  const { commit, dependencies } = useEmergencyFundStep('customCoverage');
  const {
    monthlyExpenses: { monthlyExpensesAmount },
  } = dependencies;
  const { flowId } = useParams<{ flowId: string }>();

  const handleSubmit = (formValues: FormValues) => {
    commit({ targetAmount: Money(parseFloat(formValues.targetAmount)) });
    void navigate(`/add_purpose/emergency_fund/${flowId}/next_step`);
  };

  return (
    <WorkflowLayout pageTitle="Emergency Fund Coverage">
      <WorkflowLayout.Header workflowTitle={null} onClose={() => navigate('/')} onBack={() => navigate(-1)} />
      <WorkflowLayout.Content>
        <Box css={{ mb: 'space.200' }}>
          <H1>How much do you want to save?</H1>
        </Box>

        <Formik
          onSubmit={handleSubmit}
          initialValues={{ targetAmount: '' }}
          validationSchema={schema}
          validateOnChange={true}
        >
          <Form>
            <CustomCoverageInput monthlyExpensesAmount={parseFloat(monthlyExpensesAmount)} />
            <Box css={{ mt: 'space.400' }}>
              <Button type="submit" variant="solidPrimary" css={{ mt: 'space.0' }}>
                Continue
              </Button>
            </Box>
          </Form>
        </Formik>
      </WorkflowLayout.Content>
    </WorkflowLayout>
  );
};
